import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Salesforce, MembershipTypes } from 'mxp-schemas';
import { Admin as AdminUtils } from 'mxp-utils';
import {
  employersSelector,
  isAuthSelector,
  employmentDataUpdateLoadingSelector,
  searchResultOrganizationsSelector,
} from 'modules/user/selectors';
import { isAdminPortalSelector } from 'modules/app/selectors';
import {
  setMembershipPackageOrganization,
  sendPartnerInvite,
  updateAccountAsPartner,
  createAttestation,
  searchOrganizationsByWildcard,
  searchOrganizationsByWildcardLoading,
  upsertEmploymentData,
} from 'modules/user/actions';
import { setMembershipEvent } from 'modules/membership/actions';
import { getOrganizationMembershipsSelector } from 'modules/centerAdmin/selectors';
import { MembershipPackageAccordionStatus, StorageNames } from '../../../constants';
import { getCenterMembershipPackagePath } from 'utils';
import { CenterMembershipCard } from 'components/atoms';
import {
  PersonOrganisationsCards,
  PersonOrganisationsCards_STATUS,
  PartnersInvitationSent,
  PartnersSendInvitation,
  ContactAuditQualityCentersTeam,
  CaqCentersTeamStatusEnum,
  PartnerDeclareAcknowledgement,
  PartnerDeclareAdditionalInfo,
} from 'components/molecules';
import { findOrganizationMembership } from 'utils/helpers';
import { getLocalStorageItem, LOCAL_STORAGE } from 'utils/localStorage';
import { userMembershipTypeSelector } from 'modules/membership/selectors';

interface Props {
  applEmploymentFormContainer: React.ReactElement;
}

export const CenterMembershipOrganizationSelection: React.FC<Props> = ({ applEmploymentFormContainer }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const userMembershipTypeChoice = useSelector(userMembershipTypeSelector);

  const unfilteredEmployers: Omit<Salesforce.AccountPersonAccount, 'personAccount'>[] = useSelector(employersSelector);
  const updateEmployerLoading = useSelector(employmentDataUpdateLoadingSelector);
  const searchResultOrganizations: { name: string; type: string; id?: string }[] =
    useSelector(searchResultOrganizationsSelector) ?? [];
  const ownEmployers = Array.from(
    unfilteredEmployers
      .reduce((map, employer) => {
        const existingEmployer = map.get(employer.organization.id);
        if (!existingEmployer || existingEmployer.relationshipType === MembershipTypes.RelationshipTypeEnum.ADMIN) {
          map.set(employer.organization.id, employer);
        }
        return map;
      }, new Map())
      .values()
  );

  const organizationMemberships: Salesforce.FirmMembership[] = useSelector(getOrganizationMembershipsSelector);
  const userMembershipTypeChoiceLocalStorage = getLocalStorageItem(
    StorageNames.centerMembershipPackageType,
    LOCAL_STORAGE.AICPA_DATA
  );
  const [createOrganization, setCreateOrganization] = useState(false);
  const [invitedOrganizationName, setInvitedOrganizationName] = useState<string | null>(null);
  const [isUnderReview, setIsUnderReview] = useState(false);

  // When arriving via a partner invite link, silently search for the invited organization by name
  // in the background (same lookup a manual "search or create a firm" would use) so it can be
  // listed as a selectable card alongside the user's own employers, without opening that form.
  useEffect(() => {
    const [, queryString] = location.hash.split('?');
    if (!queryString) return;

    const params = new URLSearchParams(queryString);
    const organizationName = params.get('organizationName');
    if (!organizationName) return;

    const alreadyOwnEmployer = ownEmployers.some(
      ({ organization: org }) => org?.name?.toLowerCase() === organizationName.toLowerCase()
    );
    if (!alreadyOwnEmployer) {
      setInvitedOrganizationName(organizationName);
      dispatch(searchOrganizationsByWildcardLoading());
      dispatch(searchOrganizationsByWildcard(organizationName));
    }
    // Only ever run once on mount for the initial invite link — not on every employers/location change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invitedOrganizationMatch = invitedOrganizationName
    ? searchResultOrganizations.find(
        org => org?.name?.toLowerCase() === invitedOrganizationName.toLowerCase()
      )
    : null;

  const employers = invitedOrganizationMatch
    ? [...ownEmployers, { id: invitedOrganizationMatch.id, jobTitle: '', organization: invitedOrganizationMatch }]
    : ownEmployers;
  // toggle pop-up
  // Tracks exactly which organization the invite modal is currently for — the modal is rendered
  // once, outside the per-employer .map(), so it can't accidentally bind to the wrong org's data.
  const [activeInviteContext, setActiveInviteContext] = useState<{
    organization: Salesforce.Organization; jobTitle: string;
  } | null>(null);
  const [partnerSendInvitationModalOpen, setPartnerSendInvitationModalOpen] = useState(false);
  const [partnerInvitationSentModalOpen, setPartnerInvitationSentModalOpen] = useState(false);
  const [partnerDeclareAcknowledgeOpen, setPartnerDeclareAcknowledgeOpen] = useState(false);
  const [partnerDeclareAdditionalInfoOpen, setPartnerDeclareAdditionalInfoOpen] = useState(false);
  const [isAcknowledgementConfirmed, setisAcknowledgementConfirmed] = useState(false);
  const [centersTeamNonUsModalOpen, setCentersTeamNonUsModalOpen] = useState(false);
  const [centersTeamGenericModalOpen, setCentersTeamGenericModalOpen] = useState(false);

  const [activeCompany, setActiveCompany] = useState('');

  // Attestation text
  const ATTESTATION_STATEMENTS: string[] = [
    'I am a Partner of this firm or other individual with authority to bind the firm with respect to the performance of a professional services engagement,',
    'This firm is not currently enrolled in a practice monitoring program (e.g., peer review),',
    'This firm is not the result of the purchase, sale, merger, or dissolution ...',
    'This firm has not performed in the past 12 months, does not currently perform, and does not intend to perform any of the following types of engagements:',
    'Engagements under the Statements on Auditing Standards (SASs): e.g., audits of nonissuers;',
    'Engagements under Government Auditing Standards (GAS): e.g., financial audits, attestation engagements (examination, review, or agreed-upon procedures) under GAS;',
    'Examinations under the Statements on Standards for Attestation Engagements (SSAEs): e.g., examination engagements, review engagements, agreed-upon procedure engagements;',
    'Engagements under PCAOB standards: e.g., audits of non-SEC Issuers, attestation engagements;',
    'Services under Statements on Standards for Accounting and Review Services (SSARSs): e.g., preparation, compilation, and review engagements;',
    'Services under the SSAEs not listed above.',
    'None of the owners/Partners/others with authority to bind this firm are currently associated with firms that perform the types of engagements listed above.',
  ];

  const buildUserAttestationText = ({
    firmName,
    choice,
    additionalInfo,
  }: {
    firmName: string;
    choice: 'A' | 'B';
    additionalInfo?: { question1?: string; question2?: string };
  }) => {
    const submittedAt = new Date().toISOString();
    const header = `Peer Review Attestation (User Acceptance) — Choice: ${choice} — SubmittedAt: ${submittedAt}`;
    const firmLine = firmName ? `Firm (as entered): ${firmName}` : `Firm (as entered): (not provided)`;
    const statements = [
      'Statements presented to the user:',
      ...ATTESTATION_STATEMENTS.map((s, i) => `${i + 1}. ${s}`),
    ].join('\n');
    const extras =
      choice === 'B'
        ? `\nAdditional information provided:\n- ${additionalInfo?.question1 || ''}\n- ${
            additionalInfo?.question2 || ''
          }`
        : '';
    return `${header}\n${firmLine}\n\n${statements}${extras}`;
  };

  // pop-up state
  const [partnerInformation, setPartnerInformation] = useState({
    email: '',
  });
  const [partnerOption, setPartnerOption] = useState({ optionValueA: false, optionValueB: false });
  const [partnerAdditionalInformation, setPartnerAdditionalInformation] = React.useState({
    question1: '',
    question2: '',
  });

  const toggleCreateOrganization = () => {
    setCreateOrganization(!createOrganization);
  };

  const acknowledgementConfirmed = () => {
    setisAcknowledgementConfirmed(!isAcknowledgementConfirmed);
  };
  // TODO - for future use, disabled for now
  const togglePartnerDeclareAcknowledgement = (company: string = '', isBusinessNew: boolean | null = true) => {
    if (Boolean(isBusinessNew)) {
      setActiveCompany(company);
      setPartnerDeclareAcknowledgeOpen(!partnerDeclareAcknowledgeOpen);
    } else {
      setisAcknowledgementConfirmed(true);
    }
  };
  const togglePartnerAdditionalInfo = () => {
    setPartnerDeclareAcknowledgeOpen(false);
    setPartnerDeclareAdditionalInfoOpen(!partnerDeclareAdditionalInfoOpen);
  };
  const togglePartnerSendInvitationModal = () => {
    setPartnerSendInvitationModalOpen(!partnerSendInvitationModalOpen);
  };
  const togglePartnerInvitationSentModal = () => {
    setPartnerInvitationSentModalOpen(!partnerInvitationSentModalOpen);
  };
  const toggleCentersTeamNonUsOrCaq = () => {
    setCentersTeamNonUsModalOpen(!centersTeamNonUsModalOpen);
  };
  const toggleCentersTeamGeneric = () => {
    setCentersTeamGenericModalOpen(!centersTeamGenericModalOpen);
  };
  const onPartnerSendInvitationCancel = () => {
    setActiveInviteContext(null);
    togglePartnerSendInvitationModal();
  };
  const onPartnerSendInvitationConfirm = () => {
    const organization = activeInviteContext?.organization;
    togglePartnerSendInvitationModal();
    togglePartnerInvitationSentModal();
    dispatch(setMembershipPackageOrganization({ ...organization, jobTitle: activeInviteContext?.jobTitle }));
    dispatch(sendPartnerInvite(partnerInformation, { id: organization?.id, name: organization?.name }));
    setActiveInviteContext(null);
  };
  const onCentersTeamNonUsModalCancel = () => {
    setCentersTeamNonUsModalOpen(!centersTeamNonUsModalOpen);
  };
  const onCentersTeamGenericModalCancel = () => {
    setCentersTeamGenericModalOpen(!centersTeamGenericModalOpen);
  };
  const onPartnerDeclareAcknowledgementCancel = () => {
    togglePartnerDeclareAcknowledgement();
  };
  const onPartnerDeclareAdditionalInfoCancel = () => {
    setPartnerDeclareAcknowledgeOpen(false);
    togglePartnerAdditionalInfo();
  };

  // Persist user-level attestation on A; open extra questions on B
  const onPartnerDeclareAcknowledgementConfirm = async () => {
    if (partnerOption.optionValueB) {
      setPartnerDeclareAdditionalInfoOpen(prev => !prev);
    } else {
      const attText = buildUserAttestationText({
        firmName: activeCompany,
        choice: 'A',
      });
      await dispatch(createAttestation(attText));
    }
    setisAcknowledgementConfirmed(true);
    setPartnerDeclareAcknowledgeOpen(false);
  };

  // Persist user-level attestation on B after extra info
  const onPartnerDeclareAdditionalInfoConfirm = async () => {
    togglePartnerAdditionalInfo();
    setPartnerDeclareAcknowledgeOpen(false);
    acknowledgementConfirmed();
    if (partnerOption.optionValueB) {
      // Set under review only only when option B is selected?
      setIsUnderReview(true);
      const attText = buildUserAttestationText({
        firmName: activeCompany,
        choice: 'B',
        additionalInfo: partnerAdditionalInformation,
      });
      await dispatch(createAttestation(attText));
    }
    setPartnerOption({ optionValueA: false, optionValueB: false });
    setPartnerAdditionalInformation({ question1: '', question2: '' });
  };

  const onPartnerSentConfirm = () => {
    togglePartnerInvitationSentModal();
    setPartnerInformation({ email: '' });
  };

  const toggleAccountAsPartner = async (organization: any) => {
    // update Account as a partner if relationshipType is Employee
    const employerEmployeeType = unfilteredEmployers.find(
      unfilteredEmployer =>
        unfilteredEmployer.organization.id === organization.id &&
        unfilteredEmployer.relationshipType === MembershipTypes.RelationshipTypeEnum.EMPLOYEE
    );
    if (employerEmployeeType?.id) {
      await dispatch(updateAccountAsPartner(employerEmployeeType.id));
      return;
    }

    // No employer relationship exists yet for this org (e.g. it was found via the background
    // invite-link search rather than being one of the user's own employers) — create one as an
    // Employee first, then declare it as Partner/Owner.
    const created: { processEmployment?: { id: string; organization?: { id: string } }[] } = await dispatch(
      upsertEmploymentData(
        {
          organization,
          jobTitle: '',
          isPrimary: false,
          status: 'Active',
          relationshipType: MembershipTypes.RelationshipTypeEnum.EMPLOYEE,
        } as any,
        false,
        true
      )
    );
    const newEmployerRecord = created?.processEmployment?.find(
      employer => employer.organization?.id === organization.id
    );
    if (newEmployerRecord?.id) {
      await dispatch(updateAccountAsPartner(newEmployerRecord.id));
    }
  };

  const partnerStatus = PersonOrganisationsCards_STATUS.CAN_APPLY;
  const nonPartnerStatus = PersonOrganisationsCards_STATUS.NEEDS_PARTNER;
  const alreadyMemberStatus = PersonOrganisationsCards_STATUS.ALREADY_MEMBER;
  const caqTeamStatus = PersonOrganisationsCards_STATUS.CONTACT;

  const isAdmin = useSelector(isAdminPortalSelector);
  const isAuth = useSelector(isAuthSelector);
  const isImpersonated = Boolean(isAuth) && isAdmin;
  const selectedMembershipType =
    typeof userMembershipTypeChoiceLocalStorage === 'string' ? userMembershipTypeChoiceLocalStorage : null;
  return (
    <StyledOrganizationContainer>
      {/*
    - Moved the component outside employers.map()
    - Modal should appear when creating the 1st organization
  */}
      {partnerDeclareAcknowledgeOpen && (
        <PartnerDeclareAcknowledgement
          onCancel={onPartnerDeclareAcknowledgementCancel}
          optionState={partnerOption}
          setOptionState={setPartnerOption}
          testId="partner-declare-acknowledgement"
          open={partnerDeclareAcknowledgeOpen}
          setIsUnderReview={setIsUnderReview}
          header={'Acknowledgement'}
          onConfirm={onPartnerDeclareAcknowledgementConfirm}
          confirmButtonLabel="Confirm"
          firmName={activeCompany}
        />
      )}

      {partnerSendInvitationModalOpen && (
        <PartnersSendInvitation
          onCancel={onPartnerSendInvitationCancel}
          personObject={partnerInformation}
          setPersonObject={setPartnerInformation}
          testId="partner-send-invitation"
          open={partnerSendInvitationModalOpen}
          header={`Invite a partner/owner in ${activeInviteContext?.organization?.name} to apply for center membership.`}
          onConfirm={onPartnerSendInvitationConfirm}
          confirmButtonLabel="Send invitation"
        />
      )}

      {employers && (
        <>
          {employers
            .map(employer => {
              const orgMembership = findOrganizationMembership(
                { id: employer.organization?.id, parentId: employer.organization?.parentId },
                (organizationMemberships ?? []).map(m => ({
                  accountKey: m?.accountKey,
                  firmMembershipType: m?.firmMembershipType,
                  status: m?.status,
                  admin: m?.admin ? { parentId: m.admin.parentId } : undefined,
                })),
                selectedMembershipType
              );

              return {
                ...employer,
                ...(orgMembership && { admin: orgMembership.admin }),
                hasMembership: Boolean(orgMembership),
              };
            })
            .map(({ id, jobTitle, organization, admin, hasMembership, hasRejectedApplication }: any) => {
              if (hasRejectedApplication) return null;
              const company = organization?.name ?? '';
              const underReview = organization?.underReview;

              const address = organization?.billingAddress;
              const employer = {
                street: address?.addressLine1 ?? '',
                city: address?.city ?? '',
                cityCode: address?.zipCode ?? '',
                state: address?.state ?? '',
                phoneNumber: organization?.phone ?? '',
              };
              const organizationUserChoice: unknown = {
                ...organization,
                jobTitle,
              };

              // Will allows us to "Proceed to this Firm" to the Organization if:
              // it is a USA address
              // user selects CAQ
              // is not a Government type
              // Public Accounting type for all Center Membership
              // Consulting Firm for PCPS and CPEA only
              const employerCountry = organization.billingAddress.country;
              const employerType = organization.type;
              const isCountryUS = employerCountry === 'USA' || employerCountry === 'US';
              const isNonUSorCAQ = isCountryUS || AdminUtils.isCAQType(userMembershipTypeChoice);
              const isGov = employerType === 'Government';
              const isCpeaCaqCondition =
                AdminUtils.isCPEAType(userMembershipTypeChoice) &&
                (employerType === 'Other Professional Organization' || employerType === 'FLP University' || isGov);
              const isPcpsCaqConditions =
                AdminUtils.isPCPSType(userMembershipTypeChoice) &&
                employerType !== 'Consulting Firm' &&
                employerType !== 'Public Accounting';

              const isCaqConditions =
                (AdminUtils.isGAQCType(userMembershipTypeChoice) ||
                  AdminUtils.isCAQType(userMembershipTypeChoice) ||
                  AdminUtils.isEBPAQCType(userMembershipTypeChoice)) &&
                !(employerType === 'Public Accounting' || employerType === 'Government');

              const handlePartnerProceed = () => {
                dispatch(setMembershipPackageOrganization(organizationUserChoice));
                dispatch(setMembershipEvent('isClickedCenterMembershipJourney', true));
                history.push(getCenterMembershipPackagePath(MembershipPackageAccordionStatus.CPA));
              };
              const handleNonPartnerInvite = () => {
                setActiveInviteContext({ organization, jobTitle });
                togglePartnerSendInvitationModal();
              };
              const onCaqTeamNonUsOrCaq = () => {
                toggleCentersTeamNonUsOrCaq();
              };
              const onCaqTeamGeneric = () => {
                toggleCentersTeamGeneric();
              };
              const handleNonPartnerDeclare = async () => {
                setActiveCompany(company);
                await toggleAccountAsPartner(organization);
              };

              return (
                <React.Fragment key={id}>
                  {partnerDeclareAdditionalInfoOpen && (
                    <PartnerDeclareAdditionalInfo
                      onCancel={onPartnerDeclareAdditionalInfoCancel}
                      additionalInformation={partnerAdditionalInformation}
                      setAdditionalInformation={setPartnerAdditionalInformation}
                      testId="partner-additional-information"
                      open={partnerDeclareAdditionalInfoOpen}
                      header={'Acknowledgement'}
                      onConfirm={onPartnerDeclareAdditionalInfoConfirm}
                      confirmButtonLabel="Confirm"
                    />
                  )}
                  {centersTeamNonUsModalOpen && (
                    <ContactAuditQualityCentersTeam
                      onCancel={onCentersTeamNonUsModalCancel}
                      testId="partner-invitation-sent"
                      open={centersTeamNonUsModalOpen}
                      header={'You cannot proceed with this purchase'}
                      onClose={onCentersTeamNonUsModalCancel}
                      closeButtonLabel="I understand"
                      status={CaqCentersTeamStatusEnum.NON_US_OR_CAQ}
                      centerMembershipType={userMembershipTypeChoice}
                    />
                  )}
                  {centersTeamGenericModalOpen && (
                    <ContactAuditQualityCentersTeam
                      onCancel={onCentersTeamGenericModalCancel}
                      testId="partner-invitation-sent"
                      open={centersTeamGenericModalOpen}
                      header={'You cannot proceed with this purchase'}
                      onClose={onCentersTeamGenericModalCancel}
                      closeButtonLabel="I understand"
                      status={CaqCentersTeamStatusEnum.GENERIC_CONDITIONS}
                      centerMembershipType={userMembershipTypeChoice}
                    />
                  )}
                  {partnerInvitationSentModalOpen && (
                    <PartnersInvitationSent
                      testId="partner-invitation-sent"
                      open={partnerInvitationSentModalOpen}
                      header={'Your invitation has been sent'}
                      onConfirm={onPartnerSentConfirm}
                      confirmButtonLabel="Return to center membership overview"
                    />
                  )}
                  {(() => {
                    if (AdminUtils.isCPEAType(userMembershipTypeChoice) && !hasMembership) {
                      if (!isImpersonated && (!isCountryUS || isCpeaCaqCondition)) {
                        return (
                          <PersonOrganisationsCards
                            centerMembershipType={userMembershipTypeChoice}
                            company={company}
                            employer={employer}
                            status={caqTeamStatus}
                            handleCentersTeamModal={!isCountryUS ? onCaqTeamNonUsOrCaq : onCaqTeamGeneric}
                            isUnderReview={underReview}
                            handleProceed={handlePartnerProceed}
                            isOrgNotUS={!isCountryUS && !isGov}
                          />
                        );
                      }
                      if (jobTitle === MembershipTypes.RolesNames.Partner) {
                        return (
                          <PersonOrganisationsCards
                            company={company}
                            employer={employer}
                            status={partnerStatus}
                            handleProceed={handlePartnerProceed}
                            isUnderReview={underReview}
                          />
                        );
                      }
                      return (
                        <PersonOrganisationsCards
                          centerMembershipType={userMembershipTypeChoice}
                          company={company}
                          employer={employer}
                          status={nonPartnerStatus}
                          handleInvite={handleNonPartnerInvite}
                          handleDeclare={handleNonPartnerDeclare}
                          isUnderReview={underReview}
                        />
                      );
                    }
                    if (jobTitle === MembershipTypes.RolesNames.Partner && !hasMembership) {
                      if (!isImpersonated && (!isNonUSorCAQ || isPcpsCaqConditions || isCaqConditions)) {
                        return (
                          <PersonOrganisationsCards
                            centerMembershipType={userMembershipTypeChoice}
                            company={company}
                            employer={employer}
                            status={caqTeamStatus}
                            isUnderReview={underReview}
                            handleCentersTeamModal={isNonUSorCAQ ? onCaqTeamGeneric : onCaqTeamNonUsOrCaq}
                            {...(!isCountryUS && !isGov && { isOrgNotUS: true })}
                          />
                        );
                      }
                      return (
                        <PersonOrganisationsCards
                          company={company}
                          employer={employer}
                          status={partnerStatus}
                          isUnderReview={underReview}
                          handleProceed={handlePartnerProceed}
                        />
                      );
                    }
                    if (hasMembership) {
                      return (
                        <PersonOrganisationsCards
                          centerMembershipType={userMembershipTypeChoice}
                          company={company}
                          admin={admin}
                          employer={employer}
                          status={alreadyMemberStatus}
                          isUnderReview={underReview}
                        />
                      );
                    }
                    return (
                      <PersonOrganisationsCards
                        centerMembershipType={userMembershipTypeChoice}
                        company={company}
                        employer={employer}
                        status={nonPartnerStatus}
                        handleInvite={handleNonPartnerInvite}
                        handleDeclare={handleNonPartnerDeclare}
                        isUnderReview={underReview}
                      />
                    );
                  })()}
                </React.Fragment>
              );
            })}
        </>
      )}
      {createOrganization ? (
        <StyledFirmCard>
          {React.cloneElement(applEmploymentFormContainer, {
            toggleCreateOrganization,
            togglePartnerDeclareAcknowledgement,
            isAcknowledgementConfirmed,
            acknowledgementConfirmed,
            isUnderReview,
            setIsUnderReview,
            onCancelClick: toggleCreateOrganization,
          })}
        </StyledFirmCard>
      ) : (
        <CenterMembershipCard
          title={'Cannot find your firm?'}
          description={
            'Start by searching for your employer’s name. If it does not pull up, you can create a new firm/organization record.'
          }
          loading={updateEmployerLoading}
          buttonLabel={'Search or create a Firm'}
          handleButtonChange={toggleCreateOrganization}
        />
      )}
    </StyledOrganizationContainer>
  );
};
const StyledOrganizationContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${props => props.theme.pxToRem(20)};
  color: ${props => props.theme.colors.neutralBlack};
  margin-top: ${props => props.theme.pxToRem(-50)};
  margin-left: ${props => props.theme.pxToRem(30)};

  ${props => props.theme.mediaQueries.mobileOnly} {
    width: ${props => props.theme.pxToRem(310)};
    margin-top: ${props => props.theme.pxToRem(35)};
    margin-left: ${props => props.theme.pxToRem(22)};
  }

  ${props => props.theme.mediaQueries.tabletOnly} {
    width: 40%;
  }
`;

const StyledFirmCard = styled.div`
  box-sizing: border-box;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
  min-height: ${props => props.theme.pxToRem(250)};
  background-color: ${props => props.theme.colors.neutralWhite};
  width: 95%;

  ${props => props.theme.mediaQueries.mobileOnly} {
    width: 100%;
  }
`;
